import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { WORK_PACKAGES, TENDER_STATUS_CONFIG, CATEGORY_CONFIG } from './pipelineData';
import WorkPackageDrawer from './WorkPackageDrawer';
import { ROLES } from 'config/roleConfig';

export default function ProcurementSection({ userRole, onAddToast }) {
  const [workPackages] = useState(WORK_PACKAGES);
  const [selectedWP, setSelectedWP] = useState(null);

  const canEdit = userRole === ROLES.INVESTMENT_TEAM || userRole === ROLES.PLATFORM_ADMIN;

  const handleAddWP = () => {
    if (!canEdit) {
      onAddToast?.({ type: 'info', title: 'Read-only access', message: 'Your role does not allow adding work packages.' });
    } else {
      onAddToast?.({ type: 'success', title: 'Work package added', message: 'New work package created successfully.' });
    }
  };

  return (
    <div className="mt-8 pb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Procurement</h2>
            <p className="text-sm text-slate-500 mt-0.5">Active work packages and tender management</p>
          </div>
          <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
            {workPackages?.length} Active
          </span>
        </div>
        <button
          onClick={handleAddWP}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            canEdit
              ? 'bg-[#3b4c4c] hover:bg-[#2e3d3d] text-white' :'bg-slate-100 text-slate-400 cursor-not-allowed'
          }`}
        >
          <Icon name="Plus" size={16} />
          Add Work Package
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Work Package', 'Associated Deal', 'Category', 'Est. Value', 'Tender Status', 'Bidders', 'Award Decision', 'Target Award', 'Actions']?.map((col) => (
                  <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {workPackages?.map((wp) => {
                const tStatus = TENDER_STATUS_CONFIG?.[wp?.tenderStatus];
                const cat = CATEGORY_CONFIG?.[wp?.category];
                return (
                  <tr key={wp?.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-800">{wp?.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-[#3b4c4c] font-medium whitespace-nowrap">{wp?.associatedDealName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                        style={{ color: cat?.color, background: cat?.bg }}
                      >
                        {wp?.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-bold text-slate-800">{String.fromCharCode(163)}{wp?.estimatedValue}m</span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border"
                        style={{ color: tStatus?.color, background: tStatus?.bg, borderColor: tStatus?.border }}
                      >
                        {wp?.tenderStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Icon name="Users" size={14} color="#94a3b8" />
                        <span className="text-sm text-slate-700">{wp?.bidderCount}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${
                        wp?.awardDecision === 'Pending' ? 'text-slate-400 italic' : 'text-slate-800 font-semibold'
                      }`}>
                        {wp?.awardDecision}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600 whitespace-nowrap">
                        {wp?.targetAwardDate
                          ? new Date(wp?.targetAwardDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelectedWP(wp)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-[rgba(59,76,76,0.07)] hover:text-[#2e3d3d] transition-colors"
                        >
                          <Icon name="Eye" size={12} />
                          View
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => onAddToast?.({ type: 'info', title: 'Edit mode', message: 'Editing ' + wp?.name })}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                          >
                            <Icon name="Pencil" size={12} />
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedWP && (
        <WorkPackageDrawer
          wp={selectedWP}
          userRole={userRole}
          onClose={() => setSelectedWP(null)}
          onAddToast={onAddToast}
        />
      )}
    </div>
  );
}
